<script setup lang="ts">
import { computed } from 'vue'
import { useDisplay } from 'vuetify'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  modelValue?: boolean
  fullscreen?: boolean
  mobileFullscreen?: boolean
}>(), {
  modelValue: false,
  fullscreen: false,
  mobileFullscreen: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { smAndDown } = useDisplay()

const resolvedFullscreen = computed(
  () => props.fullscreen || (props.mobileFullscreen && smAndDown.value),
)
</script>

<template>
  <VDialog
    v-bind="$attrs"
    class="app-dialog"
    :fullscreen="resolvedFullscreen"
    :model-value="props.modelValue"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <slot />
  </VDialog>
</template>

<style>
.app-dialog > .v-overlay__scrim {
  background: #11120f !important;
  opacity: 0.56 !important;
}

.app-dialog > .v-overlay__content {
  max-width: calc(100vw - 40px);
  max-height: calc(100dvh - 40px);
  margin: 20px;
}

.app-dialog > .v-overlay__content:focus-visible {
  outline: 0 !important;
}

.app-dialog > .v-overlay__content > * {
  min-width: 0;
  max-width: 100%;
  max-height: calc(100dvh - 40px);
  border: 1px solid var(--kit-border-strong) !important;
  border-radius: var(--kit-radius-overlay) !important;
  color: var(--kit-ink);
  background: var(--kit-surface-raised) !important;
  box-shadow: var(--kit-shadow-float) !important;
}

.app-dialog > .v-overlay__content > .v-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-dialog > .v-overlay__content > .v-card > .v-card-title,
.app-dialog > .v-overlay__content > .v-card > .v-card-item:first-child {
  flex: 0 0 auto;
  min-height: 68px;
  padding: 17px 22px;
  color: var(--kit-ink);
  background: var(--kit-surface-raised);
  box-shadow: inset 0 -1px var(--kit-border);
  font-size: 1.08rem;
  font-weight: 760;
  line-height: 1.25;
  overflow-wrap: anywhere;
  white-space: normal;
}

.app-dialog > .v-overlay__content > .v-card > .v-card-item:first-child .v-card-title {
  padding: 0;
  box-shadow: none;
}

.app-dialog > .v-overlay__content > .v-card > .v-toolbar:first-child {
  flex: 0 0 auto;
  min-height: 64px;
  border-bottom: 1px solid var(--kit-border);
  color: var(--kit-ink);
  background: var(--kit-surface-raised) !important;
}

.app-dialog > .v-overlay__content > .v-card > :is(.v-card-title, .v-card-item, .v-toolbar):first-child + .v-divider {
  display: none;
}

.app-dialog > .v-overlay__content > .v-card > .v-card-text {
  min-height: 0;
  padding: 22px;
  color: var(--kit-text);
  background: var(--kit-surface);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.app-dialog > .v-overlay__content > .v-card > .v-card-actions {
  flex: 0 0 auto;
  gap: 8px;
  min-height: 68px;
  padding: 12px 22px max(14px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--kit-border);
  background: var(--kit-surface-raised);
}

.app-dialog > .v-overlay__content > .v-card > .v-divider {
  border-color: var(--kit-border);
  opacity: 1;
}

.app-dialog :is(.v-card-title, .v-card-item, .v-toolbar) .v-btn--icon {
  border: 1px solid transparent;
  border-radius: var(--kit-radius-control);
  color: var(--kit-ink);
}

.app-dialog :is(.v-card-title, .v-card-item, .v-toolbar) .v-btn--icon:hover {
  border-color: var(--kit-border);
  background: var(--kit-subtle);
}

.app-dialog.v-dialog--fullscreen > .v-overlay__content,
.app-dialog.v-dialog--fullscreen > .v-overlay__content > * {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  margin: 0;
}

.app-dialog.v-dialog--fullscreen > .v-overlay__content > * {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

@media (max-width: 959.98px) {
  .app-dialog.v-dialog--fullscreen > .v-overlay__content {
    width: 100vw !important;
    height: 100dvh !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
  }

  .app-dialog.v-dialog--fullscreen > .v-overlay__content > * {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-title,
  .app-dialog > .v-overlay__content > .v-card > .v-card-item:first-child {
    min-height: 62px;
    padding: max(14px, env(safe-area-inset-top)) 16px 14px;
  }

  .app-dialog > .v-overlay__content > .v-card > .v-toolbar:first-child {
    min-height: 62px;
    padding-top: env(safe-area-inset-top);
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-text {
    flex: 1 1 auto;
    padding: 18px 16px;
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-actions {
    flex-wrap: wrap;
    min-height: 0;
    padding: 12px 16px max(14px, env(safe-area-inset-bottom));
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-actions > .v-spacer {
    display: none;
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-actions > .v-btn {
    flex: 1 1 150px;
    min-width: 0;
  }
}

@media (max-width: 420px) {
  .app-dialog > .v-overlay__content > .v-card > .v-card-text {
    padding-inline: 14px;
  }

  .app-dialog > .v-overlay__content > .v-card > .v-card-actions {
    padding-inline: 14px;
  }
}
</style>
